import { PERMISSIONS, type RepositoryIntelligenceSuggestedTask } from '@ashniva/types';
import { Alert, Button } from '@ashniva/ui';
import { useState } from 'react';
import { Link } from 'react-router';

import { usePermission } from '../../auth/session-context';
import { useProposeTasks } from '../api-changes';
import { useWorkspace } from '../workspace-context';
import { ProblemNotice } from './ProblemNotice';

/**
 * The analysis's suggested tasks, to choose from and propose for the project (P14-07). Only the chosen numbers go to
 * Desk; the text comes from the analysis on the server. Each becomes a work-plan proposal that an admin, project
 * manager or team lead publishes or rejects in the project's plan — nothing is created or assigned here.
 */
export function SuggestedTasks({
  analysisId,
  suggestions,
}: {
  analysisId: string;
  suggestions: RepositoryIntelligenceSuggestedTask[];
}) {
  const { linkId, projectId } = useWorkspace();
  const canPropose = usePermission(PERMISSIONS.PROJECT_READ);
  const [chosen, setChosen] = useState<number[]>([]);
  const propose = useProposeTasks(linkId, analysisId);

  if (suggestions.length === 0) {
    return null;
  }
  const toggle = (ordinal: number) =>
    setChosen((current) =>
      current.includes(ordinal)
        ? current.filter((value) => value !== ordinal)
        : [...current, ordinal],
    );

  return (
    <section aria-label="Suggested tasks" className="ri-stack">
      <h4>Suggested tasks</h4>
      <fieldset className="ri-list" disabled={!canPropose || propose.isPending}>
        <legend className="sr-only">Choose the tasks to propose</legend>
        {suggestions.map((task) => (
          <label key={task.ordinal} className="ri-check ri-list__item">
            <input
              type="checkbox"
              checked={chosen.includes(task.ordinal)}
              onChange={() => toggle(task.ordinal)}
            />
            <span className="ri-stack">
              <strong>{task.title}</strong>
              <span>{task.description}</span>
              {task.paths.length > 0 ? (
                <span className="ri-mono">{task.paths.join(', ')}</span>
              ) : null}
            </span>
          </label>
        ))}
      </fieldset>
      {canPropose ? (
        <div>
          <Button
            variant="primary"
            disabled={chosen.length === 0 || propose.isPending}
            disabledReason="Choose at least one task"
            loading={propose.isPending}
            onClick={() => {
              if (!propose.isPending) {
                propose.mutate([...chosen].sort((a, b) => a - b));
              }
            }}
          >
            Propose for the project
          </Button>
        </div>
      ) : (
        <p className="ri-muted">Proposing tasks needs access to the project.</p>
      )}
      {propose.isError ? <ProblemNotice error={propose.error} size="sm" /> : null}
      {propose.isSuccess ? (
        <Alert tone="success" title="Proposed">
          <ul className="ri-list">
            {propose.data.proposals.map((proposal) => (
              <li key={proposal.proposalId}>
                {proposal.title} ·{' '}
                {proposal.status === 'PENDING'
                  ? 'waiting for review'
                  : proposal.status.toLowerCase()}
              </li>
            ))}
          </ul>
          <p>
            They wait in the project’s Summary until an admin, project manager or team lead
            publishes or rejects them.{' '}
            <Link to={`/projects/${encodeURIComponent(projectId)}`}>Open the project</Link>
          </p>
        </Alert>
      ) : null}
    </section>
  );
}
